import { Link } from "react-router-dom";
import { submitKangstudyForm } from "@/lib/submit-form";
import { useState } from "react";

const footerLinks = {
  company: {
    title: "강스터디",
    links: [
      { label: "브랜드 소개", href: "/about" },
      { label: "강사 소개", href: "/about/instructor" },
      { label: "오시는 길", href: "/location" },
    ],
  },
  education: {
    title: "교육과정",
    links: [
      { label: "무료강의", href: "/courses?type=free" },
      { label: "유료강의", href: "/courses?type=paid" },
      { label: "실전반", href: "/courses?type=practical" },
      { label: "오프라인 특강", href: "/courses?type=offline" },
    ],
  },
  services: {
    title: "서비스",
    links: [
      { label: "경매 데이터", href: "/auctions" },
      { label: "물건분석 리포트", href: "/reports" },
      { label: "1:1 상담", href: "/consultation" },
      { label: "동영상 강의", href: "/lectures" },
      { label: "블로그", href: "/blog" },
    ],
  },
  support: {
    title: "고객지원",
    links: [
      { label: "공지사항", href: "/community/notice" },
      { label: "자주 묻는 질문", href: "/faq" },
      { label: "문의하기", href: "/contact" },
    ],
  },
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setSubscribeStatus("loading");
    const { ok } = await submitKangstudyForm("newsletter", {
      email,
      source: "footer_subscription",
    });
    if (ok) {
      setSubscribeStatus("success");
      setEmail("");
    } else {
      setSubscribeStatus("error");
    }
    setTimeout(() => setSubscribeStatus("idle"), 3000);
  };

  return (
    <footer className="bg-[#1C1917] text-[#B8B3AE]">
      <div className="w-full px-4 md:px-6 py-14 md:py-16 max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-[#C4956A] flex items-center justify-center">
                <i className="ri-auction-line text-lg text-white"></i>
              </div>
              <span className="text-xl font-bold text-white">강스터디</span>
            </Link>
            <p className="text-sm leading-relaxed mb-5">
              초보도 따라오는 실전 경매 공부, 강스터디와 함께라면 누구나 부동산
              경매 투자의 첫걸음을 시작할 수 있습니다.
            </p>

            {/* Newsletter */}
            <div>
              <p className="text-sm font-medium text-white mb-3">
                뉴스레터 구독하고 강의 할인 받기
              </p>
              <form
                onSubmit={handleSubscribe}
                data-readdy-form
                className="flex gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일 주소"
                  className="flex-1 px-3 py-2 text-sm bg-white/10 border border-[#3D3935] rounded-md text-white placeholder-[#8C8780] focus:outline-none focus:border-[#C4956A] transition-colors"
                  required
                />
                <button
                  type="submit"
                  disabled={subscribeStatus === "loading"}
                  className="px-4 py-2 text-sm font-medium bg-[#C4956A] text-white rounded-md hover:bg-[#B08050] transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  {subscribeStatus === "loading"
                    ? "처리중..."
                    : subscribeStatus === "success"
                    ? "완료!"
                    : subscribeStatus === "error"
                    ? "실패"
                    : "구독"}
                </button>
              </form>
            </div>
          </div>

          {/* Link Columns */}
          {Object.values(footerLinks).map((col, idx) => (
            <div key={idx}>
              <h4 className="text-sm font-semibold text-white mb-4">
                {col.title}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map((link, linkIdx) => (
                  <li key={linkIdx}>
                    <Link
                      to={link.href}
                      className="text-sm hover:text-[#C4956A] transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#2D2A26] flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-4 text-xs">
            <span>&copy; 2026 강스터디. All rights reserved.</span>
            <div className="hidden sm:block text-[#3D3935]">|</div>
            <div className="flex gap-3">
              <Link to="/privacy" className="hover:text-[#C4956A] transition-colors">
                개인정보처리방침
              </Link>
              <Link to="/terms" className="hover:text-[#C4956A] transition-colors">
                이용약관
              </Link>
              <Link to="/admin" className="hover:text-[#C4956A] transition-colors">
                관리자
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://youtube.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#2D2A26] hover:bg-[#C4956A] transition-colors text-white text-sm"
              aria-label="YouTube"
            >
              <i className="ri-youtube-fill"></i>
            </a>
            <a
              href="https://blog.naver.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#2D2A26] hover:bg-[#C4956A] transition-colors text-white text-sm"
              aria-label="Blog"
            >
              <i className="ri-article-line"></i>
            </a>
            <a
              href="https://open.kakao.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#2D2A26] hover:bg-[#C4956A] transition-colors text-white text-sm"
              aria-label="KakaoTalk"
            >
              <i className="ri-kakao-talk-fill"></i>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}